import { useEffect } from "react";
import { Icon as FunctionalIcon, type IconName } from "../ui/Icon.js";
import { useT } from "../i18n/index.js";
import { useSession } from "../state/session.js";
import { serverNow } from "../state/serverTime.js";
import { conversationPreview, stampLabel } from "./conversationModel.js";
import { useSocial } from "./state.js";

interface Row {
  id: string;
  name: string;
  icon: IconName;
  preview: string;
  at: number | null;
  unread: number;
  testId: string;
}

/** Messenger-style list of conversations: Global, System and every DM with preview, time and unread badge (D67). */
export function ConversationList() {
  const t = useT();
  const me = useSession((s) => s.account);
  const chat = useSocial((s) => s.chat);
  const channel = useSocial((s) => s.channel);
  const messages = useSocial((s) => s.messages);
  const pendingDm = useSocial((s) => s.pendingDm);
  const openChannel = useSocial((s) => s.openChannel);
  const refreshChat = useSocial((s) => s.refreshChat);
  // The list needs the chat state for DM and alliance rows; a websocket recovery clears it, so fetch whenever it is missing.
  useEffect(() => {
    if (chat === null) void refreshChat();
  }, [chat, refreshChat]);
  const now = serverNow();
  const labels = { battle: t("social.chat.battleCard"), unit: t("social.chat.unitCard"), deleted: t("social.chat.deleted") };
  const last = (id: string) => {
    const list = messages[id];
    return list?.[list.length - 1];
  };
  const loadedPreview = (id: string) => {
    const m = last(id);
    if (!m) return { preview: t("social.chat.noMessages"), at: null };
    const own = m.accountId === me?.id;
    return { preview: `${own ? t("social.chat.you") : ""}${conversationPreview(m, labels)}`, at: m.createdAt };
  };
  const rows: Row[] = [
    { id: "global", name: t("social.chat.global"), icon: "globe", unread: chat?.unread.global ?? 0, testId: "channel-global", ...loadedPreview("global") },
    { id: "system", name: t("social.chat.system"), icon: "info", unread: chat?.unread.system ?? 0, testId: "channel-system", ...loadedPreview("system") },
    ...(chat?.alliance ? [{ id: chat.alliance.channel, name: `[${chat.alliance.tag}] ${chat.alliance.name}`, icon: "people" as IconName, unread: chat.alliance.unread, testId: "channel-alliance", ...loadedPreview(chat.alliance.channel) }] : []),
    ...(chat?.dms ?? []).map((d): Row => {
      const loaded = last(d.channel);
      return { id: d.channel, name: d.name, icon: "person", unread: d.unread, testId: "channel-dm", ...(loaded ? loadedPreview(d.channel) : { preview: d.lastText, at: d.lastAt }) };
    }),
  ];
  if (pendingDm && !rows.some((r) => r.id === pendingDm.channel)) rows.push({ id: pendingDm.channel, name: pendingDm.name, icon: "person", unread: 0, testId: "channel-dm", ...loadedPreview(pendingDm.channel) });
  const stamp = (at: number | null) => {
    if (at === null) return "";
    const s = stampLabel(at, now);
    return s.kind === "yesterday" ? t("social.chat.yesterday") : s.text;
  };
  return (
    <div className="conv-list" data-testid="conversation-list">
      <div className="conv-list-head">
        <strong>{t("social.chat.conversations")}</strong>
        <span className="hint">{t("social.chat.retention", { h: chat?.retentionHours.global ?? 6 })}</span>
      </div>
      {rows.map((r) => (
        <button key={r.id} type="button" className="conv-row" data-active={r.id === channel} onClick={() => void openChannel(r.id)} data-testid={r.testId}>
          <span className="conv-avatar" aria-hidden="true">
            <FunctionalIcon name={r.icon} />
          </span>
          <span className="conv-main">
            <span className="conv-name">{r.name}</span>
            <span className="conv-preview" data-testid="conv-preview">{r.preview}</span>
          </span>
          <span className="conv-meta">
            <span className="mono">{stamp(r.at)}</span>
            {r.unread > 0 ? <span className="badge warn" data-testid="conv-unread">{r.unread}</span> : null}
          </span>
        </button>
      ))}
    </div>
  );
}
